import { State } from 'src/redux/reducers/RootReducer';
import makeActionCreator from 'src/lib/makeActionCreator';
import Debug from 'src/lib/Debug';
import {
  deleteTicketData,
  downloadTicketsData,
  DownloadTicketsPayload,
  getEventTicketsData,
  getTicketData,
  postTicketData,
  putTicketData,
  sendTicketsData,
  SendTicketsPayload,
} from 'src/apis/TicketAPI';
import { setAlert } from 'src/redux/actions/alertActions';
import { setFormStatus } from 'src/redux/actions/formActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { TicketModel } from 'src/models/TicketModel';
import { getEntitiesAction } from 'src/redux/actions/querybuilderActions';
import { resetSelections } from 'src/redux/actions/selectionActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';

type Callback = () => void;

export const SET_TAKEN_IDENTIFIERS = 'SET_TAKEN_IDENTIFIERS';
export const setTakenIdentifiers = makeActionCreator(SET_TAKEN_IDENTIFIERS);

export const SET_TICKET = 'SET_TICKET';
export const setTicket = makeActionCreator(SET_TICKET);

export const getEventTicketsAction:
(eventId: number, productId: number) => ThunkedAction<State> =
(eventId: number, productId: number) => async (dispatch: any, getState: any) => {
  if (!eventId || !productId) {
    return;
  }
  try {
    dispatch(setFormStatus({ [FORM.identifiers]: FORM_STATUS.loading }));
    const organisation = getState().currentOrganisation.id;
    const response = await getEventTicketsData(organisation, eventId, productId);
    if (response.success) {
      const identifiers = (response.data?.result || response.data || [])
        .map((ticket: any) => ticket['event_tickets.ticket_identifier'])
        .filter(Boolean);
      dispatch(setTakenIdentifiers(identifiers));
      dispatch(setFormStatus({ [FORM.identifiers]: FORM_STATUS.success }));
    } else {
      dispatch(setFormStatus({ [FORM.identifiers]: FORM_STATUS.error }));
    }
  } catch (e) {
    Debug.errorLog(e);
    dispatch(setFormStatus({ [FORM.identifiers]: FORM_STATUS.error }));
  }
};

export const getTicketAction: (ticketId: number) => ThunkedAction<State> =
(ticketId: number) => async (dispatch: any, getState: any) => {
  if (!ticketId) {
    return;
  }
  try {
    dispatch(setFormStatus({ [FORM.edit_ticket]: FORM_STATUS.loading }));
    const organisation = getState().currentOrganisation.id;
    const response = await getTicketData(organisation, ticketId);
    if (response.success) {
      dispatch(setTicket(response.data as TicketModel));
      dispatch(setFormStatus({ [FORM.edit_ticket]: FORM_STATUS.success }));
    } else {
      dispatch(setFormStatus({ [FORM.edit_ticket]: FORM_STATUS.error }));
    }
  } catch (e) {
    Debug.errorLog(e);
    dispatch(setFormStatus({ [FORM.edit_ticket]: FORM_STATUS.error }));
  }
};

export const putTicketAction:
(ticket: TicketModel, callback?: Callback) => ThunkedAction<State> =
(ticket: TicketModel, callback?: Callback) => async (dispatch: any, getState: any) => {
  try {
    const organisation = getState().currentOrganisation.id;
    dispatch(setFormStatus({ [FORM.edit_ticket]: FORM_STATUS.processing }));
    const response = await putTicketData(organisation, ticket);
    if (response.success) {
      dispatch(setTicket(response.data as TicketModel));
      dispatch(setFormStatus({ [FORM.edit_ticket]: FORM_STATUS.success }));
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        message: 'Ticket successfully saved',
      }));
      if (callback) {
        callback();
      }
      return;
    }
    dispatch(setFormStatus({ [FORM.edit_ticket]: FORM_STATUS.error }));
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      message: 'Error saving ticket',
    }));
  } catch (e) {
    dispatch(setFormStatus({ [FORM.edit_ticket]: FORM_STATUS.error }));
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      message: 'Error saving ticket',
    }));
    Debug.errorLog(e);
  }
};

export const postTicketAction:
(ticket: TicketModel, callback?: Callback) => ThunkedAction<State> =
(ticket: TicketModel, callback?: Callback) => async (dispatch: any, getState: any) => {
  try {
    const organisation = getState().currentOrganisation.id;
    dispatch(setFormStatus({ [FORM.add_ticket]: FORM_STATUS.processing }));
    const response = await postTicketData(organisation, ticket);
    if (response.success) {
      dispatch(setFormStatus({ [FORM.add_ticket]: FORM_STATUS.success }));
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        code: 'messages.ticket_created',
      }));
      if (callback) {
        callback();
      }
      return;
    }
    dispatch(setFormStatus({ [FORM.add_ticket]: FORM_STATUS.error }));
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.ticket_created_error',
    }));
  } catch (e) {
    dispatch(setFormStatus({ [FORM.add_ticket]: FORM_STATUS.error }));
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.ticket_created_error',
    }));
    Debug.errorLog(e);
  }
};

export const deleteTicketAction: (id: number) => ThunkedAction<State> =
  (id: number) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const response = await deleteTicketData(organisation, id);
      if (response.success) {
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.ticket_deleted',
        }));
        dispatch(getEntitiesAction(ENTITIES.tickets));
        dispatch(resetSelections({ entity: ENTITIES.tickets }));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.ticket_deleted_error',
      }));
    } catch (e) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.ticket_deleted_error',
      }));
      Debug.errorLog(e);
    }
  };

export const downloadTicketsAction:
(payload: DownloadTicketsPayload) => ThunkedAction<State> =
(payload: DownloadTicketsPayload) => async (dispatch: any, getState: any) => {
  try {
    const organisation = getState().currentOrganisation.id;
    const ticketIds = payload.ticket_ids ?? [];
    const filename = ticketIds.length === 1 ? `ticket_${ticketIds[0]}.pdf` : 'tickets.pdf';
    dispatch(setAlert({
      type: ALERT_TYPE.download,
      code: 'messages.download_started',
    }));
    const response = await downloadTicketsData(organisation, payload, filename);
    if (response.status === 200) {
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        code: 'messages.download_success',
      }));
      return;
    }
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.download_error',
    }));
  } catch (e) {
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.download_error',
    }));
    Debug.errorLog(e);
  }
};

export const sendTicketsAction:
(payload: SendTicketsPayload, callback?: Callback) => ThunkedAction<State> =
(payload: SendTicketsPayload, callback?: Callback) => async (dispatch: any, getState: any) => {
  try {
    const organisation = getState().currentOrganisation.id;
    const response = await sendTicketsData(organisation, payload);
    if (response.success) {
      dispatch(setAlert({
        type: ALERT_TYPE.success,
        code: 'messages.ticket_sent',
      }));
      dispatch(getEntitiesAction(ENTITIES.tickets));
      dispatch(resetSelections({ entity: ENTITIES.tickets }));
      if (callback) {
        callback();
      }
      return;
    }
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.ticket_sent_error',
    }));
  } catch (e) {
    dispatch(setAlert({
      type: ALERT_TYPE.error,
      code: 'messages.ticket_sent_error',
    }));
    Debug.errorLog(e);
  }
};
